import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import debounce from "lodash/debounce";
import { useSelector } from "react-redux";
import { getFormValues } from "redux-form";
import { withScriptjs } from "react-google-maps";
import { StandaloneSearchBox } from "react-google-maps/lib/components/places/StandaloneSearchBox";
import { GOOGLE_MAPS_API_KEY, MILE_IN_METRES, DEFAULT_SEARCH_RADIUS_IN_METRES } from "../../constants";
import Loading from "../Loading";
import VenueCard from "./VenueCard";

const RADIUS_OPTIONS = [
  { text: "Quarter of a mile", value: 0.25 },
  { text: "Half a mile", value: 0.5 },
  { text: "1 mile", value: 1 },
  { text: "2 miles", value: 2 },
  { text: "3 miles", value: 3 },
  { text: "4 miles", value: 4 },
  { text: "5 miles", value: 5 },
  { text: "6 miles", value: 6 },
  { text: "7 miles", value: 7 },
  { text: "8 miles", value: 8 },
  { text: "9 miles", value: 9 },
  { text: "10 miles", value: 10 },
  { text: "20 miles", value: 20 },
  { text: "30 miles", value: 30 },
  { text: "40 miles", value: 40 },
  { text: "50 miles", value: 50 },
  { text: "60 miles", value: 60 },
  { text: "70 miles", value: 70 },
  { text: "80 miles", value: 80 },
  { text: "90 miles", value: 90 },
  { text: "100 miles", value: 100 },
  { text: "150 miles", value: 150 },
  { text: "200 miles", value: 200 },
].map(o => ({ text: o.text, value: Math.round(o.value * MILE_IN_METRES) }));

const LocationInput = withScriptjs(({ value, onChange, onPlaceFound }) => {
  const searchBoxRef = useRef(null);

  const onPlacesChanged = () => {
    const places = searchBoxRef.current.getPlaces();
    const placeRaw = Array.isArray(places) && places[0];
    if (!placeRaw) return;
    const place = JSON.parse(JSON.stringify(placeRaw));
    const lat = place.geometry.location.lat;
    const lng = place.geometry.location.lng;
    const name = place.formatted_address;
    const addressType = place.types && place.types[0];
    onChange(name);
    onPlaceFound(lat, lng, name, addressType);
  };

  return (
    <StandaloneSearchBox ref={searchBoxRef} onPlacesChanged={onPlacesChanged}>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Enter location..."
        className="py-0 border border-grey-md w-full outline-none font-normal text-sm text-black focus:border-primary bg-white rounded-none appearance-none"
        style={{
          backgroundImage: "url(/img/map-marker.svg)",
          paddingLeft: 40,
          backgroundPosition: "10px center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "20px 20px",
          lineHeight: "37px",
          height: "37px",
        }}
      />
    </StandaloneSearchBox>
  );
});

const LocationInputWithScript = props => (
  <LocationInput
    loadingElement={<div />}
    containerElement={<div />}
    googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&v=3.exp&libraries=geometry,drawing,places`}
    {...props}
  />
);

const MatchingVenuesSearch = ({ form, onCountChange, roomStyleOptions = [], onAddVenue, onRemoveVenue, addedVenueIds = [] }) => {
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [locationLabel, setLocationLabel] = useState("");
  const [addressType, setAddressType] = useState(null);
  const [radius, setRadius] = useState(DEFAULT_SEARCH_RADIUS_IN_METRES);
  const [results, setResults] = useState([]);
  const [resultCount, setResultCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);

  const formValues = useSelector(getFormValues(form));
  const delegates = formValues?.maximum_number_of_delegates || formValues?.number_of_delegates;
  const bedroomsRequired = formValues?.bedrooms_required;
  const bedroomDates = formValues?.bedroom_dates || [];
  const exhibitionSpace = formValues?.exhibition_space;
  const backProjection = formValues?.back_projection;
  const roomStyleId = formValues?.room_style_id;

  const totalBedrooms = bedroomsRequired && bedroomDates.length
    ? Math.max(...bedroomDates.map(d => Number(d.quantity) || 0))
    : 0;

  const layoutName = roomStyleId
    ? roomStyleOptions.find(o => o.value === parseInt(roomStyleId, 10))?.text
    : null;

  const radiusText = RADIUS_OPTIONS.find(o => o.value === radius)?.text || "";
  const searchSummary = delegates && hasSearched
    ? [
        `${resultCount} venue${resultCount !== 1 ? "s" : ""} within ${radiusText} of ${locationLabel} that can support ${delegates} delegate${Number(delegates) !== 1 ? "s" : ""}`,
        layoutName ? `in a ${layoutName} layout` : null,
        totalBedrooms > 0 ? `with ${totalBedrooms} bedroom${totalBedrooms !== 1 ? "s" : ""}` : null,
        exhibitionSpace ? "with exhibition space" : null,
        backProjection ? "with back projection" : null,
      ]
        .filter(Boolean)
        .join(", ")
    : null;

  const searchRef = useRef(
    debounce(async ({ lat, lng, locationLabel, addressType, radius, delegates, totalBedrooms, exhibitionSpace, backProjection, layoutName, onCountChange }) => {
      if (!delegates) {
        return;
      }
      setLoading(true);
      const params = {
        location: locationLabel,
        location_type: "Map",
        lat,
        lng,
        capacity: delegates,
        sortBy: "ContentUpdate",
        radius,
      };
      if (addressType) params.addressType = addressType;
      if (totalBedrooms > 0) params.total_bedrooms = totalBedrooms;
      if (exhibitionSpace) params.exhibition = "1";
      if (backProjection) params.back_projection = "1";
      if (layoutName) params.layout = layoutName;
      try {
        const response = await axios.get(`${window.NODE_AS}/`, { params });
        const venues = response.data?.data || [];
        const count = response.data?.count ?? venues.length;
        setResults(venues);
        setSelectedVenue(venues[0] || null);
        setResultCount(count);
        onCountChange(count);
        setHasSearched(true);
      } catch (e) {
        setResults([]);
        setSelectedVenue(null);
        setResultCount(0);
        onCountChange(0);
        setHasSearched(true);
      } finally {
        setLoading(false);
      }
    }, 400)
  );

  useEffect(() => {
    if (lat == null || lng == null) return;
    searchRef.current({ lat, lng, locationLabel, addressType, radius, delegates, totalBedrooms, exhibitionSpace, backProjection, layoutName, onCountChange });
  }, [lat, lng, radius, delegates, totalBedrooms, exhibitionSpace, backProjection, layoutName]);

  useEffect(() => () => searchRef.current.cancel(), []);

  const onPlaceFound = (lat, lng, name, addressType) => {
    setLat(lat);
    setLng(lng);
    setAddressType(addressType);
  };

  return (
    <div>
      <div className="flex items-end mb-4">
        <div className="flex-1 mr-4">
          <label className="block font-heading text-sm uppercase mb-1">
            Search for a destination
          </label>
          <LocationInputWithScript
            value={locationLabel}
            onChange={setLocationLabel}
            onPlaceFound={onPlaceFound}
          />
        </div>
        <div>
          <label className="block font-normal font-heading text-sm uppercase mb-1">
            Radius
          </label>
          <div className="bg-white relative w-40">
            <i className="fal fa-angle-down absolute-center-y pin-r text-2xl mr-2 text-dark-grey" />
            <select
              value={radius}
              onChange={e => setRadius(Number(e.target.value))}
              className="pl-2 pr-8 w-full outline-none font-normal text-sm text-black border border-grey-md focus:border-primary rounded-none bg-transparent relative appearance-none"
              style={{ lineHeight: "37px", height: "37px" }}
            >
              {RADIUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value} className="text-black">{o.text}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {searchSummary && (
        <p className="text-sm italic mb-2">{searchSummary}</p>
      )}
      {loading && <Loading inline />}
      {!loading && !delegates && <>
        <p>Start entering brief details to see suggested venues based upon delegate count, room layout, bedroom requirements, exhibition space and back projection.</p>
        <p>Then choose a location above to see matching venues.</p>
      </>}
      {!loading && delegates && (lat == null || lng == null) && (
        <p>Choose a location above to see matching venues.</p>
      )}
      {!loading && hasSearched && delegates && results.length === 0 && (
        <p>No venues found matching your brief.</p>
      )}
      {!loading && results.length > 0 && (
        <div className="flex">
          <div className="w-1/2 flex-shrink-0 overflow-y-auto pr-4" style={{ maxHeight: "230px" }}>
            <ul className="mt-2 pl-0">
              {results.map(venue => {
                const alreadyAdded = addedVenueIds.includes(venue.id);
                const isSelected = selectedVenue && selectedVenue.id === venue.id;
                return (
                  <li
                    key={venue.id}
                    className={`py-1 border-b border-grey-md text-sm flex items-center ${isSelected ? "bg-lighter-blue" : ""}`}
                    style={alreadyAdded ? { backgroundColor: "#f0f9e4" } : undefined}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedVenue(isSelected ? null : venue)}
                      className="text-left flex-1 hover:text-brand truncate bg-transparent border-0 p-0 cursor-pointer outline-none focus:outline-none"
                    >
                      {venue.name}
                    </button>
                    {alreadyAdded && addedVenueIds.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => onRemoveVenue(venue.id)}
                        className="flex-shrink-0 ml-2 text-xs px-2 py-0 border border-red text-red hover:bg-red hover:text-white outline-none focus:outline-none"
                      >
                        Remove
                      </button>
                    ) : alreadyAdded ? (
                      <span className="flex-shrink-0 ml-2 text-xs px-2 py-0 text-grey-md">
                        Added
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onAddVenue(venue.id)}
                        className="flex-shrink-0 ml-2 text-xs px-2 py-0 border border-primary text-primary hover:bg-primary hover:text-white outline-none focus:outline-none"
                      >
                        Add
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
          {selectedVenue && (
            <div className="w-1/2  pl-4">
              <VenueCard venue={selectedVenue} canRemove={false} newTab removeVenue={() => {}} showDetailsOnHover={true} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MatchingVenuesSearch;
